import { useState, useCallback } from 'react';
import { DiscordSDK } from '@discord/embedded-app-sdk';

interface User {
  id: string;
  username: string;
  avatar: string | null;
}

interface UseDiscordAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  authenticate: () => Promise<void>;
  user: User | null;
}

export function useDiscordAuth(): UseDiscordAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const authenticate = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const clientId = new URLSearchParams(window.location.search).get('client_id');
      
      if (!clientId) {
        console.log('Development mode - not running in Discord');
        setIsAuthenticated(true);
        setUser({
          id: 'dev-user',
          username: 'Developer',
          avatar: null,
        });
        setIsLoading(false);
        return;
      }

      const discordSdk = new DiscordSDK(clientId);
      await discordSdk.ready();

      const { code: _code } = await discordSdk.commands.authorize({
        client_id: clientId,
        response_type: 'code',
        state: '',
        prompt: 'none',
        scope: ['identify', 'guilds'],
      });

      const { user: discordUser } = await discordSdk.commands.authenticate({
        access_token: '',
      });

      if (discordUser) {
        setUser({
          id: discordUser.id,
          username: discordUser.username,
          avatar: discordUser.avatar ?? null,
        });
        setIsAuthenticated(true);
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError('Failed to authenticate with Discord');
      setIsAuthenticated(true);
      setUser({
        id: 'dev-user',
        username: 'Developer',
        avatar: null,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isAuthenticated,
    isLoading,
    error,
    authenticate,
    user,
  };
}
